import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '@/contexts/AuthContext';
import api from '@/lib/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { Heart, Zap, Crown, Coffee, Check, X, RefreshCw, Calendar, AlertTriangle, ArrowRight, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import { CURRENCY_CONFIG, COUNTRY_TO_CURRENCY, getUserCurrency, getCurrencyConfig } from '@/lib/pricing';

export default function SupportPage() {
  const { user, checkAuth } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [recovering, setRecovering] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [reactivating, setReactivating] = useState(false);
  const [customAmount, setCustomAmount] = useState('');
  const [selectedTip, setSelectedTip] = useState(null);
  const [userCurrency, setUserCurrency] = useState('USD');

  useEffect(() => {
    if (user) {
      const detected = getUserCurrency(user);
      setUserCurrency(detected);
    }
  }, [user]);

  const currencyConfig = getCurrencyConfig(user);

  const handleCheckout = async (lookupKey, amount = null) => {
    if (!user) {
      toast.error('Please login to continue');
      navigate('/login');
      return;
    }
    setLoading(true);
    try {
      const payload = {
        lookup_key: lookupKey,
        custom_amount: amount,
        custom_currency: amount ? userCurrency.toLowerCase() : undefined,
        origin_url: window.location.origin,
        user_id: user.id || user._id,
      };
      const { data } = await api.post('/api/payments/checkout', payload);
      if (data.checkout_url) {
        window.location.href = data.checkout_url;
      } else {
        throw new Error('No checkout URL received');
      }
    } catch (error) {
      const msg = error.response?.data?.detail || 'Failed to initiate checkout';
      if (msg.includes('already have an active subscription')) {
        toast.error('You already have an active subscription. If your access isn\'t showing, click "Already paid?" below to activate it.');
      } else {
        toast.error(msg);
      }
      setLoading(false);
    }
  };

  const handleCustomTip = () => {
    const amount = parseFloat(customAmount);
    if (isNaN(amount) || amount < currencyConfig.tipMin || amount > currencyConfig.tipMax) {
      toast.error(`Please enter an amount between ${currencyConfig.symbol}${currencyConfig.tipMin} and ${currencyConfig.symbol}${currencyConfig.tipMax}`);
      return;
    }
    handleCheckout(null, amount);
  };

  const handleSubscribe = () => {
    if (!user) {
      toast.error('Please login first');
      navigate('/login');
      return;
    }
    const lookupKey = `pro_monthly_${userCurrency.toLowerCase()}`;
    handleCheckout(lookupKey);
  };

  const handleRecoverSubscription = async () => {
    if (!user) { toast.error('Please login first'); return; }
    setRecovering(true);
    try {
      const { data } = await api.post('/api/payments/recover-subscription');
      if (data.success) { toast.success(data.message || 'Subscription activated!'); await checkAuth(); }
      else { toast.error(data.message || 'No paid subscription found. Please contact support.'); }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Recovery failed. Please contact support.');
    } finally { setRecovering(false); }
  };

  const handleCancelSubscription = async () => {
    setCancelling(true);
    try {
      const { data } = await api.post('/api/payments/cancel-subscription');
      if (data.success) {
        const until = data.access_until
          ? new Date(data.access_until).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })
          : 'your billing period end';
        toast.success(`Subscription cancelled. You keep Pro access until ${until}.`);
        await checkAuth();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Cancellation failed. Please contact support.');
    } finally { setCancelling(false); }
  };

  const handleReactivateSubscription = async () => {
    setReactivating(true);
    try {
      const { data } = await api.post('/api/payments/reactivate-subscription');
      if (data.success) { toast.success(data.message || 'Subscription reactivated!'); await checkAuth(); }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Reactivation failed. Please contact support.');
    } finally { setReactivating(false); }
  };

  const proFeatures = [
    'Full Dashboard Analytics',
    'Investment Tracking',
    'SIP Management',
    'Net Worth Reports',
    'Advanced Charts',
    'Priority Support',
  ];

  return (
    <div className="min-h-screen bg-background p-6" data-testid="support-page">
      <div className="max-w-5xl mx-auto space-y-8">

        {/* Header */}
        <motion.div
          className="text-center space-y-4"
          initial={{ opacity: 0, y: -16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
        >
          <div
            className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-semibold tracking-widest uppercase"
            style={{
              border: '1px solid rgba(59,130,246,0.3)',
              color: '#60a5fa',
              backgroundColor: 'rgba(59,130,246,0.08)',
            }}
          >
            <div className="w-1.5 h-1.5 rounded-full bg-blue-400 animate-pulse" />
            Premium Membership
          </div>
          <h1 className="text-3xl sm:text-4xl font-bold font-['Outfit'] flex items-center justify-center gap-2">
            <Heart className="h-7 w-7 text-red-500" />
            Support TheMoneyMap
          </h1>
          <p className="text-muted-foreground max-w-2xl mx-auto text-sm sm:text-base">
            Help us keep building the best personal finance tool. Your support enables new features,
            better performance, and continuous improvements.
          </p>
        </motion.div>

        {/* Supporter badge banner */}
        {user?.supporter_badge && (
          <motion.div
            className="relative rounded-2xl overflow-hidden border p-5"
            style={{
              background: 'linear-gradient(135deg, #0f172a, #1e2d5a)',
              borderColor: 'rgba(59,130,246,0.25)',
              boxShadow: '0 0 30px rgba(59,130,246,0.1)',
            }}
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <div
              className="absolute top-0 right-0 w-40 h-40 pointer-events-none"
              style={{ background: 'radial-gradient(circle, rgba(59,130,246,0.2) 0%, transparent 70%)' }}
            />
            <div className="flex items-center gap-3 relative z-10">
              <div
                className="w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0"
                style={{ background: 'rgba(245,158,11,0.15)', border: '1px solid rgba(245,158,11,0.3)' }}
              >
                <Crown className="h-4 w-4 text-amber-400" />
              </div>
              <div>
                <div className="text-sm font-semibold text-white">Active Supporter</div>
                <div className="text-xs mt-0.5" style={{ color: 'rgba(147,197,253,0.6)' }}>
                  Thank you for supporting TheMoneyMap! You&apos;re awesome!
                </div>
              </div>
            </div>
          </motion.div>
        )}

        {/* ── Pro Membership Card — Dark Premium ── */}
        <motion.div
          className="relative rounded-2xl overflow-hidden"
          style={{
            background: 'linear-gradient(135deg, #0f172a 0%, #1a2d5a 45%, #0f172a 100%)',
            border: '1px solid rgba(59,130,246,0.22)',
            boxShadow: '0 0 50px rgba(59,130,246,0.12), 0 0 0 1px rgba(59,130,246,0.08), 0 20px 45px rgba(0,0,0,0.35)',
          }}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
        >
          {/* Background glow accents */}
          <div
            className="absolute -top-28 -right-28 w-80 h-80 rounded-full pointer-events-none"
            style={{ background: 'radial-gradient(circle, rgba(59,130,246,0.22) 0%, transparent 70%)' }}
          />
          <div
            className="absolute -bottom-20 -left-20 w-56 h-56 rounded-full pointer-events-none"
            style={{ background: 'radial-gradient(circle, rgba(14,165,233,0.1) 0%, transparent 70%)' }}
          />

          {/* BEST VALUE pill */}
          <div className="absolute top-5 right-5 z-10">
            <span
              className="inline-flex items-center gap-1.5 text-xs font-bold px-3 py-1 rounded-full uppercase tracking-widest"
              style={{ background: 'linear-gradient(90deg, #f59e0b, #fbbf24)', color: '#0f172a' }}
            >
              Best Value
            </span>
          </div>

          <div className="relative z-10 p-7 sm:p-9 space-y-6">
            {/* Title */}
            <div className="pr-28 sm:pr-0">
              <div className="flex items-center gap-2.5 mb-1">
                <Crown className="h-6 w-6 text-amber-400" />
                <h2 className="text-2xl font-bold text-white font-['Outfit']">Pro Membership</h2>
              </div>
              <p className="text-sm" style={{ color: 'rgba(147,197,253,0.55)' }}>Full access to all premium features</p>
            </div>

            {/* Price */}
            <div className="flex items-baseline gap-2">
              <span className="text-5xl font-extrabold text-white tracking-tight">
                {currencyConfig.symbol}{currencyConfig.subscription}
              </span>
              <span className="text-sm" style={{ color: 'rgba(147,197,253,0.45)' }}>/month + tax</span>
            </div>

            {/* Free trial badge */}
            <div
              className="inline-flex items-center gap-2.5 px-4 py-2 rounded-full"
              style={{
                border: '1px solid rgba(52,211,153,0.3)',
                backgroundColor: 'rgba(16,185,129,0.08)',
              }}
            >
              <div className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse flex-shrink-0" />
              <span className="text-sm font-medium" style={{ color: '#6ee7b7' }}>
                3 Months Free Trial — Cancel Anytime
              </span>
            </div>

            {/* Feature grid */}
            <div>
              <p className="text-sm font-medium mb-3" style={{ color: 'rgba(226,232,240,0.7)' }}>
                Unlock premium features:
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                {proFeatures.map((feature) => (
                  <div
                    key={feature}
                    className="flex items-center gap-2.5 text-sm"
                    style={{ color: 'rgba(226,232,240,0.8)' }}
                  >
                    <div
                      className="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0"
                      style={{
                        background: 'rgba(59,130,246,0.15)',
                        border: '1px solid rgba(59,130,246,0.35)',
                      }}
                    >
                      <Check className="h-3 w-3" style={{ color: '#60a5fa' }} />
                    </div>
                    <span>{feature}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* CTA button */}
            <motion.button
              onClick={handleSubscribe}
              disabled={loading || user?.subscription_status === 'active'}
              data-testid="start-trial-button"
              className="w-full py-3.5 px-6 rounded-xl font-semibold text-white flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              style={{
                background:
                  user?.subscription_status === 'active'
                    ? 'rgba(30,58,138,0.5)'
                    : 'linear-gradient(135deg, #1d4ed8 0%, #3b82f6 50%, #0ea5e9 100%)',
                boxShadow:
                  user?.subscription_status === 'active'
                    ? 'none'
                    : '0 0 30px rgba(59,130,246,0.4), 0 4px 15px rgba(59,130,246,0.25)',
              }}
              whileHover={user?.subscription_status !== 'active' ? { scale: 1.02 } : {}}
              whileTap={user?.subscription_status !== 'active' ? { scale: 0.98 } : {}}
            >
              {loading ? (
                <>
                  <div className="h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Processing...
                </>
              ) : user?.subscription_status === 'active' ? (
                <>
                  <Check className="h-4 w-4" />
                  Already Subscribed
                </>
              ) : (
                <>
                  <Zap className="h-4 w-4" />
                  Start Free Trial
                  <ArrowRight className="h-4 w-4" />
                </>
              )}
            </motion.button>

            {/* Billing disclosure — shown only before subscription */}
            {user?.subscription_status !== 'active' && (
              <p
                className="text-xs text-center"
                style={{ color: 'rgba(147,197,253,0.45)' }}
                data-testid="billing-disclosure"
              >
                After the 90-day free trial you will be charged{' '}
                <strong style={{ color: 'rgba(147,197,253,0.7)' }}>
                  {currencyConfig.symbol}{currencyConfig.subscription}&nbsp;{userCurrency}/month
                </strong>{' '}
                plus applicable taxes, billed automatically each month. Cancel any time before the trial ends to pay nothing.
              </p>
            )}

            {/* Recovery button */}
            {user && user.subscription_status !== 'active' && (
              <Button
                onClick={handleRecoverSubscription}
                disabled={recovering}
                variant="ghost"
                size="sm"
                className="w-full"
                style={{ color: 'rgba(147,197,253,0.45)' }}
                data-testid="recover-subscription-button"
              >
                {recovering ? (
                  <><RefreshCw className="h-3 w-3 mr-2 animate-spin" />Checking your payment...</>
                ) : (
                  <><RefreshCw className="h-3 w-3 mr-2" />Already paid? Click to activate your subscription</>
                )}
              </Button>
            )}
          </div>
        </motion.div>

        {/* ── Manage Subscription (active subscribers only) ── */}
        {user?.subscription_status === 'active' && (
          <Card data-testid="manage-subscription-card">
            <CardHeader>
              <CardTitle className="text-xl font-['Outfit'] flex items-center gap-2">
                <Calendar className="h-5 w-5" />
                Manage Subscription
              </CardTitle>
              <CardDescription>View and manage your Pro membership</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between p-3 rounded-lg bg-muted/50">
                <span className="text-xs text-muted-foreground">Status</span>
                {user.subscription_cancel_at ? (
                  <Badge variant="outline" className="text-xs text-orange-600 border-orange-300 py-0 px-2">
                    Cancels {new Date(user.subscription_cancel_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                  </Badge>
                ) : (
                  <Badge variant="outline" className="text-xs text-green-600 border-green-300 py-0 px-2">Active</Badge>
                )}
              </div>
              {user.subscription_current_period_end && !user.subscription_cancel_at && (
                <div className="flex items-center justify-between p-3 rounded-lg bg-muted/50" data-testid="next-renewal-row">
                  <span className="text-xs text-muted-foreground">Next renewal</span>
                  <span className="text-xs font-medium">
                    {new Date(user.subscription_current_period_end * 1000).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
                  </span>
                </div>
              )}
              <div className="flex items-center justify-between p-3 rounded-lg bg-muted/50">
                <span className="text-xs text-muted-foreground">Amount</span>
                <span className="text-xs font-medium">{currencyConfig.symbol}{currencyConfig.subscription}&nbsp;{userCurrency}/month&nbsp;+&nbsp;tax</span>
              </div>

              {user.subscription_cancel_at && (
                <div className="p-3 rounded-lg bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800 space-y-3">
                  <p className="text-sm text-orange-700 dark:text-orange-300">
                    Your subscription is set to cancel on{' '}
                    <strong>
                      {new Date(user.subscription_cancel_at).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
                    </strong>. You keep Pro access until then.
                  </p>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={handleReactivateSubscription}
                    disabled={reactivating}
                    className="w-full border-orange-300 text-orange-700 hover:bg-orange-50"
                    data-testid="reactivate-subscription-button"
                  >
                    {reactivating ? <RefreshCw className="h-3 w-3 mr-2 animate-spin" /> : null}
                    Keep My Subscription
                  </Button>
                </div>
              )}

              <Separator />

              {!user.subscription_cancel_at && (
                <div className="pt-1 space-y-2">
                  <p className="text-xs text-muted-foreground">
                    Need to cancel? You&apos;ll keep Pro access until your current billing period ends.
                  </p>
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button
                        variant="destructive"
                        size="sm"
                        className="w-full"
                        data-testid="cancel-subscription-trigger"
                      >
                        <X className="h-3 w-3 mr-2" />
                        Cancel Subscription
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle className="flex items-center gap-2">
                          <AlertTriangle className="h-5 w-5 text-destructive" />
                          Cancel your subscription?
                        </AlertDialogTitle>
                        <AlertDialogDescription className="space-y-2 pt-1">
                          <p>You&apos;ll keep <strong>Pro access until your current billing period ends</strong>. After that:</p>
                          <ul className="list-disc list-inside space-y-1 text-sm mt-2">
                            <li>Dashboard analytics will be locked</li>
                            <li>Investment &amp; SIP tracking will be hidden</li>
                            <li>Net Worth reports will be unavailable</li>
                            <li>Your data stays safe — you won&apos;t lose anything</li>
                          </ul>
                          <p className="text-sm pt-2">You can resubscribe at any time to regain access.</p>
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel data-testid="cancel-subscription-keep">
                          Keep My Subscription
                        </AlertDialogCancel>
                        <AlertDialogAction
                          onClick={handleCancelSubscription}
                          disabled={cancelling}
                          className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                          data-testid="cancel-subscription-confirm"
                        >
                          {cancelling ? <RefreshCw className="h-3 w-3 mr-2 animate-spin" /> : null}
                          Yes, Cancel
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* ── One-Time Tips ── */}
        <Card>
          <CardHeader>
            <CardTitle className="text-xl font-['Outfit'] flex items-center gap-2">
              <Coffee className="h-5 w-5 text-amber-600" />
              Buy Me a Coffee
            </CardTitle>
            <CardDescription>Show your appreciation with a one-time tip</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="grid grid-cols-3 gap-3">
              {currencyConfig.tipPresets.map((amount) => {
                const lookupKey = userCurrency === 'INR'
                  ? `tip_inr_${amount}`
                  : `tip_${userCurrency.toLowerCase()}_${amount}`;
                return (
                  <Button
                    key={lookupKey}
                    variant={selectedTip === lookupKey ? 'default' : 'outline'}
                    onClick={() => {
                      setSelectedTip(lookupKey);
                      handleCheckout(lookupKey);
                    }}
                    disabled={loading}
                    className="h-16"
                    data-testid={`tip-btn-${amount}`}
                  >
                    <div className="text-center">
                      <div className="text-lg font-bold">{currencyConfig.symbol}{amount}</div>
                    </div>
                  </Button>
                );
              })}
            </div>

            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <div className="h-px flex-1 bg-border" />
                <span className="text-xs text-muted-foreground">or enter custom amount</span>
                <div className="h-px flex-1 bg-border" />
              </div>

              <div className="flex gap-2">
                <div className="relative flex-1">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">
                    {currencyConfig.symbol}
                  </span>
                  <Input
                    type="number"
                    placeholder={`${currencyConfig.tipMin}-${currencyConfig.tipMax}`}
                    value={customAmount}
                    onChange={(e) => setCustomAmount(e.target.value)}
                    className="pl-8"
                    min={currencyConfig.tipMin}
                    max={currencyConfig.tipMax}
                    data-testid="custom-tip-input"
                  />
                </div>
                <Button
                  onClick={handleCustomTip}
                  disabled={loading || !customAmount}
                  data-testid="custom-tip-submit"
                >
                  {loading ? 'Processing...' : 'Send Tip'}
                </Button>
              </div>
              <p className="text-xs text-muted-foreground">
                Amount between {currencyConfig.symbol}{currencyConfig.tipMin} and {currencyConfig.symbol}{currencyConfig.tipMax}
              </p>
            </div>
          </CardContent>
        </Card>

        {/* ── Why Support? ── */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg font-['Outfit']">Why Support TheMoneyMap?</CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="space-y-2.5 text-sm text-muted-foreground">
              {[
                'Keep the app free and accessible for everyone',
                'Enable faster feature development and bug fixes',
                'Support open, privacy-focused financial tools',
                'Get a supporter badge on your profile',
              ].map((reason) => (
                <li key={reason} className="flex items-start gap-2">
                  <Check className="h-4 w-4 text-green-600 mt-0.5 shrink-0" />
                  <span>{reason}</span>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>

        {/* No Thanks */}
        <div className="text-center">
          <Button
            variant="ghost"
            onClick={() => navigate('/dashboard')}
            className="text-muted-foreground"
          >
            <X className="h-4 w-4 mr-2" />
            Not right now, maybe later
          </Button>
        </div>

      </div>
    </div>
  );
}
